/**
 * Activity Tracker Service
 * Tracks user activity for analytics in the admin dashboard
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { authService } from './auth.js';

class ActivityTracker {
  constructor() {
    this.queue = [];
    this.isProcessing = false;
    this.batchTimeout = null;
    this.BATCH_DELAY_MS = 2000; // Batch activities for 2 seconds
    this.MAX_QUEUE_SIZE = 10;
    this.sessionStart = Date.now();
    this.pageViewCount = 0;
    this.lastPageView = null;
  }

  /**
   * Track a page view
   * @param {string} page - Page path (e.g., '/dashboard', '/clients')
   */
  trackPageView(page) {
    // Don't track duplicate consecutive page views
    if (this.lastPageView === page) {
      return;
    }

    this.lastPageView = page;
    this.pageViewCount++;

    this._addToQueue({
      type: 'page_view',
      action: `view_${this._normalizePageName(page)}`,
      metadata: {
        page,
        pageViewNumber: this.pageViewCount,
        sessionDurationMs: Date.now() - this.sessionStart,
        referrer: document.referrer || null
      }
    });
  }

  /**
   * Normalize page name for consistent tracking
   * @param {string} page - Page path
   * @returns {string} Normalized page name
   */
  _normalizePageName(page) {
    // Remove leading slash and params
    let normalized = page.replace(/^\//, '').split('/')[0] || 'home';

    // Handle dynamic routes
    if (page.startsWith('/clients/profile/')) {
      normalized = 'client_profile';
    } else if (page.startsWith('/clients/edit/')) {
      normalized = 'client_edit';
    } else if (page.startsWith('/clients/login/')) {
      normalized = 'client_login';
    } else if (page.startsWith('/sys-admin')) {
      normalized = 'admin_dashboard';
    }

    return normalized.replace(/-/g, '_');
  }

  /**
   * Add activity to queue and schedule batch processing
   * @param {object} activity - Activity to track
   */
  _addToQueue(activity) {
    // Only track if user is authenticated
    if (!authService.currentUser) {
      return;
    }

    this.queue.push({
      ...activity,
      timestamp: new Date().toISOString()
    });

    // Process immediately if queue is full
    if (this.queue.length >= this.MAX_QUEUE_SIZE) {
      this._processQueue();
    } else {
      // Otherwise, batch activities
      this._scheduleBatch();
    }
  }

  /**
   * Schedule batch processing
   */
  _scheduleBatch() {
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
    }

    this.batchTimeout = setTimeout(() => {
      this._processQueue();
    }, this.BATCH_DELAY_MS);
  }

  /**
   * Process queued activities
   */
  async _processQueue() {
    if (this.isProcessing || this.queue.length === 0) {
      return;
    }

    this.isProcessing = true;
    const activitiesToProcess = [...this.queue];
    this.queue = [];

    try {
      const functions = getFunctions();
      const logActivity = httpsCallable(functions, 'logActivity');

      // Send each activity (could be optimized to batch in single call)
      for (const activity of activitiesToProcess) {
        try {
          await logActivity(activity);
        } catch (error) {
          // silent
        }
      }
    } catch (error) {
      // silent
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Flush any remaining activities (call on page unload)
   */
  flush() {
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
    }

    if (this.queue.length > 0) {
      // Use sendBeacon for reliable delivery on page unload
      this._processQueue();
    }
  }

  /**
   * Initialize tracker with page unload handling
   */
  init() {
    // Track on page unload
    window.addEventListener('beforeunload', () => {
      this.flush();
    });

    // Track visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flush();
      }
    });
  }
}

// Export singleton instance
export const activityTracker = new ActivityTracker();

// Initialize on import
if (typeof window !== 'undefined') {
  activityTracker.init();
}
